"use client";

import { useState, useEffect } from "react";
import { useRouter } from "src/i18n/navigation";
import { useLocale } from "next-intl";
import { useTranslations } from "next-intl";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import MuiLink from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import GoogleIcon from "@mui/icons-material/Google";
import AppleIcon from "@mui/icons-material/Apple";

const APPLE_ENABLED = process.env.NEXT_PUBLIC_FIREBASE_APPLE_ENABLED === "true";
import { authProvider } from "src/authProvider";
import { REDIRECT_TO_HOME } from "src/constants/redirect.constant";
import { safeRedirectPath } from "src/utils/login-redirect";
import * as Sentry from "@sentry/nextjs";
import nookies from "nookies";
import { signInWithFirebase, wasReportedAuthError } from "src/firebase/sign-in";
import { useInAppBrowser } from "src/hooks/useInAppBrowser";
import { InAppSignInNotice } from "@components/in-app-browser-notice/sign-in";
import {
  AuthLayout,
  AuthHeading,
  AuthSubtitle,
  AUTH_COLORS,
} from "src/components/auth-layout";

/** `?to=` from a page that sent a logged-out user here (e.g. /dogodki/…), honoured after a real sign-in; read at use time, client only. */
const returnTo = () => safeRedirectPath(new URLSearchParams(window.location.search).get("to"));

export default function Login() {
  const t = useTranslations();
  const router = useRouter();
  const locale = useLocale();
  const development = process.env.NODE_ENV === "development";

  const [email, setEmail] = useState(development ? "jernejkocica@gmail.com" : "");
  const [password, setPassword] = useState(development ? "12345678" : "");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const isAuth = await authProvider.check({});
        if (isAuth.authenticated) {
          // Not `to`: a page that 401s with a cookie still set would bounce between here and there.
          router.push(REDIRECT_TO_HOME);
        }
      } catch (err) {
        Sentry.captureException(err);
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [router]);

  const inAppBrowser = useInAppBrowser();

  const handleOAuth = async (provider: "google" | "apple") => {
    setError(null);
    setLoading(true);
    try {
      const tokens = await signInWithFirebase(provider);
      nookies.set(null, "bearerToken", tokens.access_token, {
        maxAge: 40 * 60 * 60,
        path: "/",
        sameSite: "lax",
        secure: process.env.NODE_ENV === "production",
      });
      nookies.set(null, "refreshToken", tokens.refresh_token, {
        maxAge: 180 * 24 * 60 * 60,
        path: "/",
        sameSite: "lax",
        secure: process.env.NODE_ENV === "production",
      });
      router.push(returnTo() ?? REDIRECT_TO_HOME);
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || "Sign-in failed");
      // A Firebase-step failure is already reported, tagged (sign-in.ts).
      if (!wasReportedAuthError(err)) Sentry.captureException(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const loginFn = authProvider.login;
      if (!loginFn) {
        throw new Error("login method not available");
      }

      const result = await loginFn({
        email,
        password,
      });

      if (result.success) {
        router.push(returnTo() ?? ".");
      } else {
        setError(result.error?.message || "Invalid email or password");
      }
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || "An error occurred");
      Sentry.captureException(err);
    } finally {
      setLoading(false);
    }
  };

  if (isChecking) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: AUTH_COLORS.beige,
        }}
      >
        <CircularProgress sx={{ color: AUTH_COLORS.teal }} />
      </Box>
    );
  }

  return (
    <AuthLayout
      // TODO: add i18n key "pages.login.heroTagline" — currently fallback English
      tagline="Welcome back. Your memories are waiting."
      subTagline={t("documentTitle.default")}
    >
      <AuthHeading>{t("pages.login.title")}</AuthHeading>
      <AuthSubtitle>
        {/* TODO: add i18n key "pages.login.subtitle" */}
        Sign in to access your account and orders.
      </AuthSubtitle>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: "10px" }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label={t("pages.login.fields.email")}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
          required
          autoFocus
          slotProps={{
            input: {
              sx: {
                borderRadius: "10px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: AUTH_COLORS.teal,
                },
              },
            },
            inputLabel: {
              sx: {
                "&.Mui-focused": { color: AUTH_COLORS.teal },
              },
            },
          }}
        />
        <TextField
          fullWidth
          label={t("pages.login.fields.password")}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          required
          slotProps={{
            input: {
              sx: {
                borderRadius: "10px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: AUTH_COLORS.teal,
                },
              },
            },
            inputLabel: {
              sx: {
                "&.Mui-focused": { color: AUTH_COLORS.teal },
              },
            },
          }}
        />

        <Button
          fullWidth
          variant="contained"
          size="large"
          type="submit"
          disabled={loading}
          sx={{
            mt: 3,
            mb: 2,
            borderRadius: "100px",
            backgroundColor: AUTH_COLORS.coral,
            color: "#ffffff",
            fontWeight: 600,
            fontSize: "1rem",
            textTransform: "none",
            py: 1.4,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#d04030",
              boxShadow: "0 4px 12px rgba(229,87,64,0.28)",
            },
            "&:disabled": {
              backgroundColor: AUTH_COLORS.coral,
              opacity: 0.6,
            },
          }}
        >
          {loading ? (
            <CircularProgress size={22} sx={{ color: "#fff" }} />
          ) : (
            t("pages.login.buttons.submit")
          )}
        </Button>
      </Box>

      {/* OAuth buttons */}
      <Divider sx={{ my: 2 }}>
        <Typography variant="body2" sx={{ color: AUTH_COLORS.textSecondary, px: 1 }}>
          or
        </Typography>
      </Divider>
      {/* Google refuses sign-in inside Facebook/Instagram's in-app
          browser: say so and offer the real browser instead. */}
      {inAppBrowser.app ? (
        <InAppSignInNotice platform={inAppBrowser.platform} withApple={APPLE_ENABLED} />
      ) : (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => handleOAuth("google")}
          disabled={loading}
          startIcon={<GoogleIcon />}
          sx={{ borderRadius: "100px", textTransform: "none", py: 1.4 }}
        >
          Continue with Google
        </Button>
        {APPLE_ENABLED && (
          <Button
            fullWidth
            variant="outlined"
            onClick={() => handleOAuth("apple")}
            disabled={loading}
            startIcon={<AppleIcon />}
            sx={{
              borderRadius: "100px",
              textTransform: "none",
              py: 1.4,
              backgroundColor: "#000",
              color: "#fff",
              borderColor: "#000",
              "&:hover": { backgroundColor: "#333", borderColor: "#333" },
            }}
          >
            Continue with Apple
          </Button>
        )}
      </Box>
      )}

      {/* Secondary links */}
      <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <MuiLink
          href={`/${locale}/forgot-password`}
          underline="none"
          sx={{
            color: AUTH_COLORS.teal,
            fontSize: "0.875rem",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {t("pages.login.buttons.forgotPassword")}
        </MuiLink>
        <Typography variant="body2" sx={{ color: AUTH_COLORS.textSecondary }}>
          {t("pages.login.buttons.noAccount")}{" "}
          <MuiLink
            href={`/${locale}/register`}
            underline="none"
            sx={{
              color: AUTH_COLORS.teal,
              fontWeight: 600,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {t("pages.login.buttons.signup")}
          </MuiLink>
        </Typography>
      </Box>
    </AuthLayout>
  );
}
